<div class="train-orders-container">
  <div class="page-header">
    <h1>Uttagna tågordrar</h1>
  </div>

  <!-- Search Section -->
  <div class="search-section">
    <div class="search-controls">
      <div class="date-picker-container">
        <label for="operationalDate">Driftdatum:</label>
        <app-datetime-field
          inputId="operationalDate"
          mode="date"
          [value]="selectedDate"
          (valueChange)="selectedDate = $event"
          placeholder="Välj datum">
        </app-datetime-field>
      </div>

      <app-chip-filter-field
        label="Lokförare:"
        placeholder="Sök lokförare..."
        [options]="filters.state['driver'].filtered"
        [selected]="filters.state['driver'].selected"
        [disabled]="trainInformations.length === 0"
        (inputChange)="filters.onInput('driver', $event)"
        (optionSelected)="filters.select('driver', $event); applyFilters()"
        (removed)="filters.remove('driver', $event); applyFilters()">
      </app-chip-filter-field>

      <app-chip-filter-field
        label="OTN:"
        placeholder="Sök OTN..."
        [options]="filters.state['otn'].filtered"
        [selected]="filters.state['otn'].selected"
        [disabled]="trainInformations.length === 0"
        (inputChange)="filters.onInput('otn', $event)"
        (optionSelected)="filters.select('otn', $event); applyFilters()"
        (removed)="filters.remove('otn', $event); applyFilters()">
      </app-chip-filter-field>

    </div>

    <div class="search-actions">
      <button
        mat-raised-button
        color="primary"
        class="fetch-button"
        (click)="fetchTrainInformations()"
        [disabled]="loading">
        @if (!loading) {
          <mat-icon>search</mat-icon>
        }
        @if (loading) {
          <mat-icon class="spinner">refresh</mat-icon>
        }
        {{ loading ? 'Hämtar...' : 'Hämta' }}
      </button>
      @if (filters.hasAnySelected()) {
        <button
          mat-stroked-button
          (click)="clearFilters()"
          class="clear-filters-btn"
          >
          <mat-icon>clear</mat-icon>
          Rensa filter
        </button>
      }
    </div>
  </div>

  <!-- No results when no data at all -->
  @if (trainInformations.length === 0 && !loading) {
    <div class="no-results">
      <mat-icon>info</mat-icon>
      <p>Inga tågorderuttag hittades för valt datum.</p>
    </div>
  }

  <!-- No results when filtered out -->
  @if (trainInformations.length > 0 && filteredTrainInformations.length === 0 && !loading) {
    <div class="no-results">
      <mat-icon>filter_alt_off</mat-icon>
      <p>Inga tågorderuttag matchar de valda filtren.</p>
    </div>
  }

  <!-- Results Section -->
  @if (!loading && paginatedTrainInformations.length > 0) {
    <div class="results-section">
      @if (trainInformations.length > 0) {
        <div class="results-header">
          <h2>
            Tågorderuttag
            <span class="count">({{ filteredTrainInformations.length }} av {{ trainInformations.length }})</span>
          </h2>
          @if (filteredTrainInformations.length > 0) {
            <mat-paginator class="paginator dark-results-paginator"
              [length]="filteredTrainInformations.length"
              [pageSize]="pageSize"
              [pageIndex]="pageIndex"
              [pageSizeOptions]="pageSizeOptions"
              (page)="onPageChange($event)"
              contaner-text-size
              showFirstLastButtons>
            </mat-paginator>
          }
        </div>
      }
      <!-- Table -->
      @if (filteredTrainInformations.length > 0) {
        <div class="table-container">
          <table mat-table [dataSource]="paginatedTrainInformations" class="train-orders-table">
            <!-- OTN Column -->
            <ng-container matColumnDef="otn">
              <th mat-header-cell *matHeaderCellDef class="sortable" (click)="sortBy('otn')">
                <span>OTN <mat-icon class="sort-icon">{{ getSortIcon('otn') }}</mat-icon></span>
              </th>
              <td mat-cell *matCellDef="let info">
                {{ info.operationalTrainNumber || '-' }}
              </td>
            </ng-container>
            <!-- Driver Name Column -->
            <ng-container matColumnDef="driver">
              <th mat-header-cell *matHeaderCellDef class="sortable" (click)="sortBy('driver')">
                <span>Lokförare <mat-icon class="sort-icon">{{ getSortIcon('driver') }}</mat-icon></span>
              </th>
              <td mat-cell *matCellDef="let info">
                {{ info.firstName }} {{ info.lastName }}
              </td>
            </ng-container>
            <!-- Employee ID Column -->
            <ng-container matColumnDef="employeeId">
              <th mat-header-cell *matHeaderCellDef>Anställningsnummer</th>
              <td mat-cell *matCellDef="let info">{{ info.employeeId || '-' }}</td>
            </ng-container>
            <!-- Withdrawn At Column -->
            <ng-container matColumnDef="originTime">
              <th mat-header-cell *matHeaderCellDef class="sortable" (click)="sortBy('originTime')">
                <span>Uttagstid <mat-icon class="sort-icon">{{ getSortIcon('originTime') }}</mat-icon></span>
              </th>
              <td mat-cell *matCellDef="let info">
                {{ formatTime(info.withdrawnAt) }}
              </td>
            </ng-container>
            <!-- Origin Column -->
            <ng-container matColumnDef="origin">
              <th mat-header-cell *matHeaderCellDef>Startdriftplats</th>
              <td mat-cell *matCellDef="let info">
                <div class="station-info">
                  <span class="station-name">{{ info.origin || '-' }}</span>
                  <span class="station-time">{{ formatDateTime(info.originTime) }}</span>
                </div>
              </td>
            </ng-container>
            <!-- Destination Column -->
            <ng-container matColumnDef="destination">
              <th mat-header-cell *matHeaderCellDef>Slutdriftplats</th>
              <td mat-cell *matCellDef="let info">
                <div class="station-info">
                  <span class="station-name">{{ info.destination || '-' }}</span>
                  <span class="station-time">{{ formatDateTime(info.destinationTime) }}</span>
                </div>
              </td>
            </ng-container>
            <!-- Train Order Number Column -->
            <ng-container matColumnDef="trainOrderNumber">
              <th mat-header-cell *matHeaderCellDef>Tågordernummer</th>
              <td mat-cell *matCellDef="let info">{{ info.trainOrderNumber || '-' }}</td>
            </ng-container>
            <!-- Cancel Date Column -->
            <ng-container matColumnDef="canceledAt">
              <th mat-header-cell *matHeaderCellDef>Makuleringstid</th>
              <td mat-cell *matCellDef="let info" [class.cancelled]="info.canceledAt">
                {{ info.canceledAt ? formatDateTime(info.canceledAt) : '-' }}
              </td>
            </ng-container>
            <tr mat-header-row *matHeaderRowDef="['otn', 'driver', 'employeeId', 'originTime', 'origin', 'destination', 'trainOrderNumber', 'canceledAt']"></tr>
            <tr mat-row *matRowDef="let row; columns: ['otn', 'driver', 'employeeId', 'originTime', 'origin', 'destination', 'trainOrderNumber', 'canceledAt']"></tr>
          </table>
        </div>
      }
    </div>
  }

  <!-- Loading Spinner -->
  @if (loading) {
    <div class="loading-container">
      <mat-spinner></mat-spinner>
      <p>Hämtar tågorderuttag...</p>
    </div>
  }
</div>

